import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Icon, Pane, Tooltip, ConfirmationModal } from '@folio/stripes/components';
import { AppIcon, useOkapiKy, useStripes } from '@folio/stripes-core';
import { FormattedMessage, useIntl } from 'react-intl';
import { useParams, useHistory } from 'react-router-dom';
import { getLocalizedValue } from '../../../../helpers/localizationUtils';
import MainMetaData from './components/MainMetaData';
import ReportDisplayMethods from './components/ReportDisplayMethods';
import ReportDetailsActions from './components/ReportDetailsActions';
import ReportPermissions from './components/ReportPermissions';

function ReportDetails({
  open,
  onClose,
  selectedRow,
  currentReportId,
  reportsList,
  mutator,
  history,
  location,
}) {
  const params = useParams();
  const historyNav = useHistory();
  const intl = useIntl();
  const { locale } = intl;
  const okapiKy = useOkapiKy();
  const stripes = useStripes();
  const currentUserId = stripes.user?.user?.id;
  const [selectedReport, setSelectedReport] = useState(selectedRow);
  const [categories, setCategories] = useState([]);
  const [reportTypes, setReportTypes] = useState([]);

  // Fetch categories and reportTypes
  useEffect(() => {
    okapiKy
      .get('categories?limit=1000&query=cql.allRecords=1 sortby name')
      .json()
      .then((data) => setCategories(data.categories || []))
      .catch(() => {});

    okapiKy
      .get('report-types?limit=1000&query=cql.allRecords=1 sortby name')
      .json()
      .then((data) => setReportTypes(data.reportTypes || []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (reportsList.length) {
      setSelectedReport(reportsList?.find((r) => r.id === currentReportId));
    }
  }, [currentReportId, reportsList]);

  const canRun = selectedReport?.status === 'published' || selectedReport?.status === 'scheduled';

  // Heavy report warning
  const isHeavyReport = (() => {
    const perf = selectedReport?.performance;
    if (!perf) return false;
    return perf.complexity === 'high' || perf.complexity === 'very_high'
        || perf.dataVolume === 'large' || perf.dataVolume === 'very_large';
  })();
  const [showHeavyWarning, setShowHeavyWarning] = useState(false);
  const [pendingRunPath, setPendingRunPath] = useState(null);

  // Determine if current user can edit/delete this report
  const canEdit = (() => {
    const permissions = selectedReport?.permissions;
    if (!permissions) return true; // legacy report
    const owner = permissions.owner || '';
    if (!owner) return true; // no owner set
    if (currentUserId === owner) return true;
    if (permissions.visibility === 'restricted') {
      return (permissions.editors || []).includes(currentUserId);
    }
    return false;
  })();

  const handleDelete = (reportId) => {
    mutator.reports.DELETE({ id: reportId }).then(() => {
      onClose();
      history.push('/reports/reports-list');
    });
  };

  const handlePermissionsUpdate = async (updatedPermissions) => {
    if (!selectedReport?.id) return;
    try {
      await okapiKy.put(`reports/${selectedReport.id}`, {
        json: { ...selectedReport, permissions: updatedPermissions },
      });
      setSelectedReport((prev) => ({
        ...prev,
        permissions: updatedPermissions,
      }));
    } catch (err) {
      console.error('Failed to update permissions:', err);
    }
  };

  const renderLastMenu = () => (
    <>
      <Tooltip
        id="report-details-actions-run-report-btn-tooltip"
        text={
          <FormattedMessage
            id="ui-reports.reportDetailsActions.runReportBtn.tooltip.msg"
            defaultMessage="Run the report"
          />
        }
      >
        {({ ref, ariaIds }) => (
          <Button
            marginBottom0
            id="report-details-actions-run-report-btn"
            buttonStyle="primary"
            to={canRun && !isHeavyReport ? {
              pathname: `/reports/reports-list/${selectedReport?.id}/edit-query`,
              search: '?run=true',
              state: { returnSearch: location?.search },
            } : undefined}
            onClick={canRun && isHeavyReport ? () => {
              setPendingRunPath({
                pathname: `/reports/reports-list/${selectedReport?.id}/edit-query`,
                search: '?run=true',
                state: { returnSearch: location?.search },
              });
              setShowHeavyWarning(true);
            } : (!canRun ? (e) => e.preventDefault() : undefined)}
            disabled={!canRun}
            onMouseDown={(e) => e.preventDefault()}
            aria-labelledby={ariaIds.text}
            ref={ref}
          >
            <Icon icon="play" size="large" />
          </Button>
        )}
      </Tooltip>
      <ReportDetailsActions report={selectedReport} onDelete={handleDelete} canEdit={canEdit} location={location} />
    </>
  );

  return (
    <>
      {open && (
        <Pane
          id="report-details-pane"
          appIcon={<AppIcon app="reports" />}
          defaultWidth="40%"
          paneTitle={getLocalizedValue(selectedReport?.name, locale) || ''}
          dismissible
          onClose={() => onClose()}
          lastMenu={renderLastMenu()}
        >
          <MainMetaData
            selectedReport={selectedReport}
            categories={categories}
            reportTypes={reportTypes}
          />
          <ReportDisplayMethods selectedReport={selectedReport} />
          <ReportPermissions
            selectedReport={selectedReport}
            onUpdate={handlePermissionsUpdate}
          />
        </Pane>
      )}

      {showHeavyWarning && (
        <ConfirmationModal
          open={showHeavyWarning}
          heading={
            <FormattedMessage
              id="ui-reports.report.heavyWarning.heading"
              defaultMessage="This report may take a while"
            />
          }
          message={
            <FormattedMessage
              id="ui-reports.report.heavyWarning.message"
              defaultMessage="This report has {complexity} complexity and {dataVolume} data volume. It may take longer than usual to execute. Do you want to continue?"
              values={{
                complexity: selectedReport?.performance?.complexity
                  ? intl.formatMessage({
                      id: `ui-reports.performance.complexity.${selectedReport.performance.complexity}`,
                      defaultMessage: selectedReport.performance.complexity,
                    })
                  : '',
                dataVolume: selectedReport?.performance?.dataVolume
                  ? intl.formatMessage({
                      id: `ui-reports.performance.dataVolume.${selectedReport.performance.dataVolume}`,
                      defaultMessage: selectedReport.performance.dataVolume,
                    })
                  : '',
              }}
            />
          }
          confirmLabel={
            <FormattedMessage id="ui-reports.report.heavyWarning.confirm" defaultMessage="Run anyway" />
          }
          cancelLabel={
            <FormattedMessage id="ui-reports.button.cancel" defaultMessage="Cancel" />
          }
          onConfirm={() => {
            setShowHeavyWarning(false);
            if (pendingRunPath) historyNav.push(pendingRunPath);
          }}
          onCancel={() => {
            setShowHeavyWarning(false);
            setPendingRunPath(null);
          }}
        />
      )}
    </>
  );
}

ReportDetails.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  selectedRow: PropTypes.object.isRequired,
  currentReportId: PropTypes.string,
  reportsList: PropTypes.arrayOf(PropTypes.object).isRequired,
  history: PropTypes.object.isRequired,
  location: PropTypes.object,
  mutator: PropTypes.shape({
    reports: PropTypes.shape({
      DELETE: PropTypes.func.isRequired,
    }),
  }),
};

export default ReportDetails;
